// The current stage's fields, edited on the deal's page (TX-US-0043): with the deal flow on, a deal
// writer edits the deal where it is read, beside what its next move waits on. Each field the move
// waits on is marked as needed, and a term an approved round locked is shown and not edited.
//
// One save path, as on the form (DealForm.jsx): the version the page read goes with the save, so a
// concurrent edit is refused rather than overwritten. A save that succeeds has the page read the
// deal again, and the page mounts this afresh for the new version - so what the next move waits on
// is said of what was saved. Reading the deal again without a new version - after a document is
// added - keeps what is typed here.
//
// What the next move waits on is read from the saved deal, so the page holds the move back while
// anything here is unsaved (`onDirtyChange`); saving or discarding the changes releases it.

import { useEffect, useState } from 'react';

import { updateDeal } from '../api.js';
import { useMe } from '../MeContext.js';
import { WRITER_ROLES, holdsAny } from '../roles.js';
import { notify } from '../ui/notify.jsx';
import { useUnsavedWarning } from '../ui/useUnsavedWarning.js';
import DealFieldset from './DealFieldset.jsx';
import { fromDeal, toRequestBody, validate } from './dealForm.js';
import { fieldMarks } from './fieldMarks.js';
import SaveRefusal from './SaveRefusal.jsx';
import { useSaveRefusal } from './useSaveRefusal.js';

export default function DealStageForm({
  deal, view, lists, vocabulary, onChanged, onDirtyChange,
}) {
  const me = useMe();
  // Adding a product or a unit is a reference-data write, which is OPERATIONS' alone (ADR-0012).
  const canAddEntries = holdsAny(me, WRITER_ROLES);

  const [draft, setDraft] = useState(() => fromDeal(deal));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const { refusal, fieldErrors, refuse, refuseFields } = useSaveRefusal();

  useUnsavedWarning(dirty);
  // Said to the page as it changes, and as clean once this form is gone.
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  const set = (name) => (value) => {
    setDraft((current) => ({ ...current, [name]: value }));
    setDirty(true);
  };

  const discard = () => {
    setDraft(fromDeal(deal));
    setDirty(false);
    refuseFields({});
  };

  const save = async (event) => {
    event.preventDefault();
    const refused = validate(draft, { flow: true, stage: deal.stage });
    refuseFields(refused);
    if (Object.keys(refused).length > 0) return;

    setBusy(true);
    try {
      const { deal: saved } = await updateDeal(
        deal.pre_deal_id, toRequestBody(draft, { flow: true }), deal.version,
      );
      setDirty(false);
      notify(`Deal ${saved.fields?.deal_number ?? saved.pre_deal_id} saved.`);
      // Nothing is typed until the page has the saved deal: a field changed before it arrives
      // would be lost when the page mounts this afresh for the new version.
      await onChanged();
    } catch (err) {
      refuse(err, draft, vocabulary);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="deal-form deal-stage-form" onSubmit={save} aria-label="The deal's fields">
      <SaveRefusal refusal={refusal} onReload={onChanged} />

      <fieldset className="fieldset-contents" disabled={busy}>
        <DealFieldset draft={draft} set={set} flow lists={lists} vocabulary={vocabulary}
                      fieldErrors={fieldErrors} canAddEntries={canAddEntries}
                      marks={fieldMarks(deal, view)} />
      </fieldset>

      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || !dirty}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={discard} disabled={busy || !dirty}>
          Discard changes
        </button>
        {dirty && !busy && (
          <span className="hint">Unsaved: what the deal needs to move on is read from what is saved.</span>
        )}
      </div>
    </form>
  );
}
